<template>
  <b-modal
    v-model="visible"
    :title="`File Manager - ${deviceId}`"
    size="xl"
    hide-footer
    @hidden="onHidden"
      centered
  no-enforce-focus
  >
    <div class="file-manager-container">
      <!-- Toolbar -->
      <div class="toolbar mb-3">
        <b-button-group size="sm">
         
       
          <b-button variant="secondary" @click="createNewFolder">
            <i class="fas fa-folder-plus"></i> New folder
          </b-button>
        </b-button-group>
        <b-button-group size="sm" class="ml-2">
          <b-button variant="secondary" @click="createNewFile">
            <i class="fas fa-file"></i> Create file
          </b-button>
        
        </b-button-group>
    
        <b-button
          variant="warning"
          size="sm"
          class="ml-auto"
          disabled
          title="Format USB (not implemented)"
        >
          <i class="fas fa-exclamation-triangle"></i> Format USB
        </b-button>
      </div>

      <!-- Breadcrumb -->
      <div class="breadcrumb-bar mb-2">
        <b-breadcrumb :items="breadcrumbItems" class="bg-transparent p-0" />
      </div>

      <!-- Main split view -->
      <b-row class="main-area">
        <b-col cols="3" class="tree-panel border-right">
          <div class="tree-header text-muted small text-uppercase mb-2">Folders</div>
          <div class="tree-content">
            <div
              v-for="item in treeItems"
              :key="item.path"
              :class="['tree-item', { active: currentPath === item.path }]"
              @click="selectFolder(item.path)"
            >
              <i :class="item.icon"></i>
              <span>{{ item.name }}</span>
              <i v-if="item.loading" class="fas fa-spinner fa-spin ml-auto"></i>
            </div>
          </div>
        </b-col>

        <b-col cols="9" class="file-panel">
          <b-table
            :items="files"
            :fields="fileFields"
            selectable
            select-mode="multi"
            @row-selected="onRowSelected"
            small
            hover
            responsive
            :busy="loading"
            show-empty
            empty-text="No files"
            class="file-table"
          >
            <template #table-busy>
              <div class="text-center my-4">
                <b-spinner small></b-spinner> Loading...
              </div>
            </template>
            <template #cell(name)="data">
              <i :class="data.item.isFile ? 'fas fa-file' : 'fas fa-folder'"></i>
              {{ data.value }}
            </template>
            <template #cell(size)="data">
              {{ formatSize(data.value) }}
            </template>
            <template #cell(actions)="data">
              <b-button-group size="sm">
            
                <b-button variant="outline-secondary" @click="renameItem(data.item)" title="Rename">
                  <i class="fas fa-pencil-alt"></i>
                </b-button>
                <b-button variant="outline-danger" @click="deleteItem(data.item)" title="Delete">
                  <i class="fas fa-trash"></i>
                </b-button>
              </b-button-group>
            </template>
          </b-table>
        </b-col>
      </b-row>

      <!-- Status bar -->
      <div class="status-bar mt-2 text-muted small d-flex justify-content-between">
        <span>
          {{ files.length }} items
          <span v-if="selectedFiles.length">
            · <strong>{{ selectedFiles.length }}</strong> selected
            · <code>{{ currentPath }}/{{ selectedFiles[0]?.name }}</code>
          </span>
        </span>
        <span :class="statusClass">{{ statusMessage }}</span>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { Action } from '@/store/actions';
import Swal from 'sweetalert2';

export default {
  name: 'ModalFileManager',
  props: {
    deviceId: {
      type: String,
      required: true
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: this.value,
      currentPath: '/config',      // start with /config since root / returns -9
      files: [],
      selectedFiles: [],
      loading: false,
     treeStructure: {},
      statusMessage: 'Ready',
      statusVariant: 'success',
      pollingTimeout: null
    };
  },
  computed: {
    ...mapGetters(['activeEntity']),
    fileFields() {
      return [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'size', label: 'Size', sortable: true },
        { key: 'actions', label: 'Actions' }
      ];
    },
    breadcrumbItems() {
      const parts = this.currentPath.split('/').filter(p => p);
      const items = [{ text: 'root', href: '#', onClick: () => this.selectFolder('/') }];
      let cumulative = '';
      parts.forEach(part => {
        cumulative += '/' + part;
        items.push({ text: part, href: '#', onClick: () => this.selectFolder(cumulative) });
      });
      return items;
    },
   treeItems() {
  const items = [];

  // Helper to recursively build tree
  const addNode = (nodePath, level = 0) => {
    const node = this.treeStructure[nodePath];
    if (!node) return;

    items.push({
      name: node.name,
      path: nodePath,
      icon: node.loaded ? 'fas fa-folder-open' : 'fas fa-folder',
      loading: node.loading,
      level
    });

    // If loaded, add its children that are directories
    if (node.loaded && node.children) {
      node.children
        .filter(child => !child.isFile)
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach(child => {
          const childPath = child.fullPath;
          // Ensure child node exists in treeStructure
          if (!this.treeStructure[childPath]) {
            this.$set(this.treeStructure, childPath, {
              name: child.name,
              path: childPath,
              loaded: false,
              children: [],
              loading: false
            });
          }
          addNode(childPath, level + 1);
        });
    }
  };

  // Start from root
  if (this.treeStructure['/']) {
    addNode('/');
  } else {
    // If root not loaded yet, show a loading placeholder
    items.push({
      name: 'root',
      path: '/',
      icon: 'fas fa-folder',
      loading: false
    });
  }

  return items;
},
    statusClass() {
      return {
        'text-success': this.statusVariant === 'success',
        'text-danger': this.statusVariant === 'danger',
        'text-warning': this.statusVariant === 'warning',
        'text-info': this.statusVariant === 'info'
      };
    }
  },
   mounted() {
  console.log('🟣 ModalFileManager mounted, value:', this.value);
  if (this.value) {
    this.initialize();
  }
},
watch: {
  value: {
    immediate: true,
    handler(val) {
      console.log('🟡 watch value changed:', val);
      this.visible = val;
      if (val) {
        this.initialize();
      }
    }
  },
  visible(val) {
    console.log('🟢 watch visible changed:', val);
    this.$emit('input', val);
  }
},
  methods: {
   async initialize() {
  console.log('🔴 initialize() START');
    console.log('deviceId:', this.deviceId);
  console.log('Initial treeStructure:', this.treeStructure);
  
  this.treeStructure = {};
  this.currentPath = '/';
  this.files = [];
  this.selectedFiles = [];

  console.log('Calling loadFolder("/")...');
  try {
    await this.loadFolder('/');
    console.log('loadFolder("/") succeeded');
  } catch (error) {
    console.error('loadFolder("/") failed:', error);
    console.log('Falling back to /config');
    this.statusMessage = 'Root unavailable, loading /config instead...';
    this.statusVariant = 'warning';
    this.currentPath = '/config';
    try {
      await this.loadFolder('/config');
      console.log('loadFolder("/config") succeeded');
    } catch (fallbackError) {
      console.error('loadFolder("/config") also failed:', fallbackError);
    }
  }
  console.log('=== INITIALIZE END ===');
},
    async loadFolder(path) {
          //console.log('loadFolder called with path:', path);
  //console.log('deviceId:', this.deviceId);
  //console.log('$store:', this.$store);
  //console.log('Action.device.sendFget:', Action.device.sendFget);
      // If already loaded, just switch
      const node = this.treeStructure[path];
      if (node && node.loaded) {
        this.currentPath = path;
        this.files = node.children;
        return;
      }

      this.loading = true;
      this.statusMessage = `Loading ${path}...`;
      this.statusVariant = 'info';

      try {
        // Mark loading
        if (node) {
          this.$set(node, 'loading', true);
        }

        // Send fget command
        const result = await this.$store.dispatch(Action.device.sendFget, {
          deviceName: this.deviceId,
          path
        });
        const requestId = result.request_id;

        // Poll for result
        const fgetResult = await this.pollForResult(requestId, path);

        // Process result
      // Process result
if (fgetResult.status === 'ready') {
  const content = fgetResult.content;
  const fileEntries = content.ents || [];

  // Build files array with fullPath included
  const filesWithFullPath = fileEntries.map(entry => ({
    ...entry,
    fullPath: path === '/' ? `/${entry.name}` : `${path}/${entry.name}`
  }));

  // Update or create tree node
  if (!this.treeStructure[path]) {
    this.$set(this.treeStructure, path, {
      name: path === '/' ? 'root' : path.split('/').pop(),
      path,
      loaded: true,
      children: filesWithFullPath,
      loading: false
    });
  } else {
    this.$set(this.treeStructure[path], 'loaded', true);
    this.$set(this.treeStructure[path], 'children', filesWithFullPath);
    this.$set(this.treeStructure[path], 'loading', false);
  }

  // If this is the root folder, also pre‑create placeholder nodes for any subdirectories
  if (path === '/') {
    filesWithFullPath.forEach(entry => {
      if (!entry.isFile) {
        const subPath = entry.fullPath;
        if (!this.treeStructure[subPath]) {
          this.$set(this.treeStructure, subPath, {
            name: entry.name,
            path: subPath,
            loaded: false,
            children: [],
            loading: false
          });
        }
      }
    });
  }

  this.currentPath = path;
  this.files = filesWithFullPath;
  this.statusMessage = `Loaded ${fileEntries.length} items`;
  this.statusVariant = 'success';
} else {
  throw new Error('Failed to load folder');
}
      } catch (error) {
        console.error('Load folder error:', error);
        this.statusMessage = `Error: ${error.message}`;
        this.statusVariant = 'danger';
        if (node) {
          this.$set(node, 'loading', false);
        }
        // Show error toast
        this.$notify({
          title: 'Error',
          message: `Failed to load folder ${path}: ${error.message}`,
          type: 'error'
        });
      } finally {
        this.loading = false;
      }
    },
    async pollForResult(requestId, path, timeout = 15000) {
      const start = Date.now();
      const interval = 1500;
      let attempts = 0;

      while (Date.now() - start < timeout) {
        attempts++;
        try {
          const res = await this.$store.dispatch(Action.device.getFgetResult, requestId);
          if (res.status === 'ready') {
            return res;
          }
          if (res.status === 'failed') {
            throw new Error(res.error || 'Request failed');
          }
        } catch (error) {
          // If 404, maybe still pending or expired
          if (error.response?.status === 404) {
            // continue polling
          } else {
            throw error;
          }
        }
        await new Promise(resolve => setTimeout(resolve, interval));
      }
      throw new Error(`Timeout waiting for folder listing (${timeout/1000}s)`);
    },
    selectFolder(path) {
      this.loadFolder(path);
    },
    onRowSelected(items) {
      this.selectedFiles = items;
    },
    formatSize(bytes) {
      if (!bytes) return '-';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    },
    // Action handlers (stubs for now)
   sendDownloadCommand() {
  Swal.fire({
    title: 'Send to Device',
    text: 'Enter URL for device to download:',
    input: 'url',
    showCancelButton: true,
    inputPlaceholder: 'https://example.com/file.json',
    target: document.body,
    didOpen: () => { document.querySelector('.swal2-input')?.focus(); }
  }).then(result => {
    if (result.value) {
      this.$store.dispatch(Action.device.sendMqttGeneralCommand, {
        deviceName: this.deviceId,
        payload: {
          devwork: {
            actions: [{
              a: 'fdld',
              p: { url: result.value, file: `${this.currentPath}/downloaded_file` }
            }]
          }
        }
      });
      this.$notify({ title: 'Command sent', message: 'Download command sent to device', type: 'info' });
    }
  });
},
    getSelectedFile() {
      if (this.selectedFiles.length === 0) return;
      const file = this.selectedFiles[0];
      this.getFile(file);
    },
    getFile(file) {
      // For files, we'll implement content retrieval later
      if (file.size > 3000) {
        this.$notify({
          title: 'Large File',
          message: `File too large (${this.formatSize(file.size)}) to retrieve inline.`,
          type: 'warning'
        });
        return;
      }
      // Send fget for file content
      this.$store.dispatch(Action.device.sendFget, {
        deviceName: this.deviceId,
        path: file.fullPath
      }).then(async result => {
        const content = await this.pollForResult(result.request_id, file.fullPath);
        if (content.status === 'ready') {
          // Show file content in a modal
          Swal.fire({
            title: file.name,
            text: content.content,
            confirmButtonText: 'Close'
          });
        }
      }).catch(error => {
        this.$notify({ title: 'Error', message: error.message, type: 'error' });
      });
    },
   createNewFolder() {
  Swal.fire({
    title: 'New Folder',
    text: 'Enter folder name:',
    input: 'text',
    showCancelButton: true,
    target: document.body,
    didOpen: () => { document.querySelector('.swal2-input')?.focus(); }
  }).then(result => {
    if (result.value) {
      const newPath = `${this.currentPath}/${result.value}`;
      this.$store.dispatch(Action.device.sendMqttGeneralCommand, {
        deviceName: this.deviceId,
        payload: {
          devwork: {
            actions: [{ a: 'fmkd', p: { path: newPath } }]
          }
        }
      });
      this.$notify({ title: 'Command sent', message: 'Create folder command sent', type: 'info' });
    }
  });
},
  createNewFile() {
  Swal.fire({
    title: 'New File',
    text: 'Enter file name:',
    input: 'text',
    showCancelButton: true,
    target: document.body,
    didOpen: () => { document.querySelector('.swal2-input')?.focus(); }
  }).then(result => {
    if (result.value) {
      const newPath = `${this.currentPath}/${result.value}`;
      this.$store.dispatch(Action.device.sendMqttGeneralCommand, {
        deviceName: this.deviceId,
        payload: {
          devwork: {
            actions: [{ a: 'fcre', p: { file: newPath } }]
          }
        }
      });
      this.$notify({ title: 'Command sent', message: 'Create file command sent', type: 'info' });
    }
  });
},
    renameSelected() {
      if (this.selectedFiles.length === 0) return;
      const item = this.selectedFiles[0];
      this.renameItem(item);
    },
  renameItem(item) {
  Swal.fire({
    title: 'Rename',
    text: `Enter new name for ${item.name}:`,
    input: 'text',
    inputValue: item.name,
    showCancelButton: true,
    target: document.body,
    didOpen: () => { document.querySelector('.swal2-input')?.focus(); }
  }).then(result => {
    if (result.value) {
      const oldPath = item.fullPath;
      const newPath = `${this.currentPath}/${result.value}`;
      this.$store.dispatch(Action.device.sendMqttGeneralCommand, {
        deviceName: this.deviceId,
        payload: {
          devwork: {
            actions: [{ a: 'fmv', p: { src: oldPath, dst: newPath } }]
          }
        }
      });
      this.$notify({ title: 'Command sent', message: 'Rename command sent', type: 'info' });
    }
  });
},
    deleteSelected() {
      if (this.selectedFiles.length === 0) return;
      const item = this.selectedFiles[0];
      this.deleteItem(item);
    },
   deleteItem(item) {
  Swal.fire({
    title: 'Delete',
    text: `Are you sure you want to delete ${item.name}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    confirmButtonText: 'Delete',
    target: document.body
  }).then(result => {
    if (result.isConfirmed) {
              console.log('Deleting item with path:', item.fullPath);   // ✅ Add this line

      this.$store.dispatch(Action.device.sendMqttGeneralCommand, {
        deviceName: this.deviceId,
        payload: {
          devwork: {
            actions: [{ a: 'fdel', p: { path: item.fullPath } }]
          }
        }
      });
      this.$notify({ title: 'Command sent', message: 'Delete command sent', type: 'info' });
    }
  });
},
    onHidden() {
          console.log('🔵 modal hidden');
      // Cleanup
      if (this.pollingTimeout) {
        clearTimeout(this.pollingTimeout);
      }
    }
  },
 
  beforeDestroy() {
    if (this.pollingTimeout) {
      clearTimeout(this.pollingTimeout);
    }
  }
};
</script>

<style scoped>
.file-manager-container {
  min-height: 500px;
}
.toolbar {
  display: flex;
  align-items: center;
}
.tree-panel {
  height: 400px;
  overflow-y: auto;
}
.tree-item {
  padding: 6px 12px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.tree-item:hover {
  background-color: #f5f7fa;
}
.tree-item.active {
  background-color: #e6f1fb;
  color: #185fa5;
  font-weight: 500;
}
.file-panel {
  height: 400px;
  overflow-y: auto;
}
.file-table >>> tbody tr {
  cursor: pointer;
}
.status-bar {
  padding: 8px 0;
  border-top: 1px solid #dee2e6;
}
.tree-item.child {
  padding-left: 24px;
}
</style>